import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Loader, Link2 } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import './AddRestaurant.scss';

const AddRestaurant = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { success, error } = useSnackbar();
    const [formData, setFormData] = useState({
        name: '',
        district: '',
        state: 'Kerala',
        dish: '',
        googleMapsLink: '',
        location: {
            latitude: '',
            longitude: ''
        },
        description: '',
        keywords: [],
        status: 'available',
        verified: true, // Auto-verified for admin-added restaurants
        premium: false,
        contact: {
            phone: '',
            email: '',
            website: ''
        },
        operatingHours: {
            start: '',
            end: '',
            days: []
        },
        paymentMethods: [],
        capacity: '',
        googlePlaceId: '',
        googleRating: '',
        googleReviewCount: '',
        namazFacility: false,
        womenFacility: false,
        linkedMosqueID: ''
    });
    const [loading, setLoading] = useState(false);
    const [extractingCoords, setExtractingCoords] = useState(false);
    const [nearbyMosques, setNearbyMosques] = useState([]);
    const [loadingNearby, setLoadingNearby] = useState(false);

    const dishes = ['Biriyani', 'Neychoru', 'Pathiri', 'Snacks', 'Jeeraka Kanji ജീരക കഞ്ഞി', 'Other'];
    const districts = [
        'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod',
        'Kollam', 'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad',
        'Pathanamthitta', 'Thiruvananthapuram', 'Thrissur', 'Wayanad'
    ];
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const paymentOptions = ['Cash', 'Card', 'UPI', 'Net Banking', 'Wallet'];

    // Pre-fill from mosque when navigating from MosqueDetails
    useEffect(() => {
        const suggest = location.state?.suggestMosque;
        if (suggest?.mosqueID && suggest?.location) {
            setFormData(prev => ({
                ...prev,
                name: prev.name || suggest.name ? `${suggest.name} Restaurant` : prev.name,
                district: prev.district || suggest.district,
                googleMapsLink: prev.googleMapsLink || suggest.googleMapsLink,
                location: suggest.location?.latitude ? {
                    latitude: suggest.location.latitude,
                    longitude: suggest.location.longitude
                } : prev.location,
                linkedMosqueID: suggest.mosqueID
            }));
        }
    }, [location.state]);

    // Fetch nearby mosques when coordinates change
    useEffect(() => {
        const lat = parseFloat(formData.location?.latitude);
        const lon = parseFloat(formData.location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            setNearbyMosques([]);
            return;
        }
        setLoadingNearby(true);
        api.get(`/admin/restaurants/nearby-mosques?lat=${lat}&lon=${lon}`)
            .then(res => {
                if (res.data.success) setNearbyMosques(res.data.mosques || []);
                else setNearbyMosques([]);
            })
            .catch(() => setNearbyMosques([]))
            .finally(() => setLoadingNearby(false));
    }, [formData.location?.latitude, formData.location?.longitude]);

    const handleExtractCoordinates = async () => {
        const link = formData.googleMapsLink?.trim();
        
        if (!link || link === '') {
            error('Please enter a Google Maps link first');
            return;
        }

        if (!link.startsWith('http://') && !link.startsWith('https://')) {
            error('Please enter a valid URL starting with http:// or https://');
            return;
        }

        setExtractingCoords(true);
        try {
            const response = await api.post('/admin/restaurants/extract-coordinates', {
                googleMapsLink: link
            });
            
            if (response.data.success && response.data.coordinates) {
                setFormData(prev => ({
                    ...prev,
                    location: {
                        latitude: response.data.coordinates.latitude,
                        longitude: response.data.coordinates.longitude
                    }
                }));
                success(`Coordinates extracted: ${response.data.coordinates.latitude.toFixed(6)}, ${response.data.coordinates.longitude.toFixed(6)}`);
            } else {
                error('Could not extract coordinates from the URL. Please check the link format.');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Error extracting coordinates';
            error(errorMessage);
            console.error('Extract coordinates error:', err.response?.data || err);
        } finally {
            setExtractingCoords(false);
        }
    };

    const extractCoordsFromGoogleMaps = (url) => {
        try {
            const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
            const match = url.match(regex);
            if (match) {
                return {
                    latitude: parseFloat(match[1]),
                    longitude: parseFloat(match[2])
                };
            }
            const placeRegex = /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/;
            const placeMatch = url.match(placeRegex);
            if (placeMatch) {
                return {
                    latitude: parseFloat(placeMatch[1]),
                    longitude: parseFloat(placeMatch[2])
                };
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'googleMapsLink') {
            const coords = extractCoordsFromGoogleMaps(value);
            setFormData(prev => ({
                ...prev,
                googleMapsLink: value,
                location: coords || prev.location
            }));
        } else if (name.includes('location.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                location: {
                    ...prev.location,
                    [field]: value
                }
            }));
        } else if (name.includes('contact.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                contact: {
                    ...prev.contact,
                    [field]: value
                }
            }));
        } else if (name.includes('operatingHours.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                operatingHours: {
                    ...prev.operatingHours,
                    [field]: value
                }
            }));
        } else if (name === 'keywords') {
            setFormData(prev => ({
                ...prev,
                keywords: value.split(',').map(k => k.trim()).filter(k => k)
            }));
        } else if (name === 'paymentMethod') {
            setFormData(prev => ({
                ...prev,
                paymentMethods: checked
                    ? [...prev.paymentMethods, value]
                    : prev.paymentMethods.filter(m => m !== value)
            }));
        } else if (name === 'operatingDay') {
            setFormData(prev => ({
                ...prev,
                operatingHours: {
                    ...prev.operatingHours,
                    days: checked
                        ? [...prev.operatingHours.days, value]
                        : prev.operatingHours.days.filter(d => d !== value)
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : (type === 'number' && value !== '' ? parseFloat(value) : value)
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = {
                ...formData,
                capacity: formData.capacity ? parseInt(formData.capacity) : null,
                googleRating: formData.googleRating ? parseFloat(formData.googleRating) : null,
                googleReviewCount: formData.googleReviewCount ? parseInt(formData.googleReviewCount) : 0,
                linkedMosqueID: formData.linkedMosqueID || undefined
            };
            const response = await api.post('/admin/restaurants', submitData);
            if (response.data.success) {
                success('Restaurant added successfully!');
                navigate(`/restaurants/${response.data.restaurant.restaurantID}`);
            }
        } catch (err) {
            error(err.response?.data?.message || 'Error adding restaurant');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-restaurant">
            <div className="page-header">
                <button className="back-button" onClick={() => navigate('/restaurants')}>
                    <ArrowLeft size={20} />
                    Back
                </button>
                <h2>Add New Restaurant</h2>
            </div>

            <form className="restaurant-form" onSubmit={handleSubmit}>
                <div className="form-section">
                    <h3>Basic Information</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Restaurant Name *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>District *</label>
                            <select
                                name="district"
                                value={formData.district}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select District</option>
                                {districts.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>State *</label>
                            <input
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Food Type *</label>
                            <select
                                name="dish"
                                value={formData.dish}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Food Type</option>
                                {dishes.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Google Maps Link *</label>
                        <div className="input-with-button">
                            <input
                                type="url"
                                name="googleMapsLink"
                                value={formData.googleMapsLink}
                                onChange={handleChange}
                                required
                                placeholder="https://www.google.com/maps/... or https://g.co/kgs/..."
                            />
                            <button
                                type="button"
                                className="extract-coords-btn"
                                onClick={handleExtractCoordinates}
                                disabled={extractingCoords || !formData.googleMapsLink}
                                title="Extract coordinates from URL"
                            >
                                {extractingCoords ? (
                                    <Loader size={16} className="spinning" />
                                ) : (
                                    <>
                                        <MapPin size={16} />
                                        Extract
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Latitude</label>
                            <input
                                type="number"
                                name="location.latitude"
                                value={formData.location.latitude}
                                onChange={handleChange}
                                step="any"
                            />
                        </div>

                        <div className="form-group">
                            <label>Longitude</label>
                            <input
                                type="number"
                                name="location.longitude"
                                value={formData.location.longitude}
                                onChange={handleChange}
                                step="any"
                            />
                        </div>
                    </div>

                    {nearbyMosques.length > 0 && (
                        <div className="form-group link-mosque">
                            <label><Link2 size={16} /> Link to mosque at same location</label>
                            <select
                                name="linkedMosqueID"
                                value={formData.linkedMosqueID || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, linkedMosqueID: e.target.value || '' }))}
                            >
                                <option value="">None</option>
                                {nearbyMosques.map(m => (
                                    <option key={m.mosqueID} value={m.mosqueID}>
                                        {m.name} ({m.mosqueID})
                                    </option>
                                ))}
                            </select>
                            {loadingNearby && <span className="loading-nearby">Checking...</span>}
                        </div>
                    )}

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                        />
                    </div>

                    <div className="form-group">
                        <label>Keywords (comma-separated)</label>
                        <input
                            type="text"
                            name="keywords"
                            value={Array.isArray(formData.keywords) ? formData.keywords.join(', ') : ''}
                            onChange={handleChange}
                            placeholder="keyword1, keyword2, keyword3"
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h3>Contact Information</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Phone</label>
                            <input
                                type="tel"
                                name="contact.phone"
                                value={formData.contact.phone}
                                onChange={handleChange}
                                placeholder="+91 1234567890"
                            />
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                name="contact.email"
                                value={formData.contact.email}
                                onChange={handleChange}
                                placeholder="restaurant@example.com"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Website</label>
                        <input
                            type="url"
                            name="contact.website"
                            value={formData.contact.website}
                            onChange={handleChange}
                            placeholder="https://www.example.com"
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h3>Operating Hours</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Start Time</label>
                            <input
                                type="time"
                                name="operatingHours.start"
                                value={formData.operatingHours.start}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label>End Time</label>
                            <input
                                type="time"
                                name="operatingHours.end"
                                value={formData.operatingHours.end}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Operating Days</label>
                        <div className="checkbox-group">
                            {daysOfWeek.map(day => (
                                <label key={day} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="operatingDay"
                                        value={day}
                                        checked={formData.operatingHours.days.includes(day)}
                                        onChange={handleChange}
                                    />
                                    {day}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Additional Information</h3>
                    <div className="form-group">
                        <label>Payment Methods</label>
                        <div className="checkbox-group">
                            {paymentOptions.map(method => (
                                <label key={method} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="paymentMethod"
                                        value={method}
                                        checked={formData.paymentMethods.includes(method)}
                                        onChange={handleChange}
                                    />
                                    {method}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Capacity (seating)</label>
                            <input
                                type="number"
                                name="capacity"
                                value={formData.capacity}
                                onChange={handleChange}
                                min="1"
                                placeholder="50"
                            />
                        </div>

                        <div className="form-group">
                            <label>Google Place ID</label>
                            <input
                                type="text"
                                name="googlePlaceId"
                                value={formData.googlePlaceId}
                                onChange={handleChange}
                                placeholder="ChIJ..."
                            />
                            <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                Optional: Enter Google Place ID to fetch ratings automatically. You can find it in the Google Maps URL or use the "Fetch Ratings" button after saving.
                            </small>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Google Rating (0-5)</label>
                            <input
                                type="number"
                                name="googleRating"
                                value={formData.googleRating}
                                onChange={handleChange}
                                min="0"
                                max="5"
                                step="0.1"
                                placeholder="4.5"
                            />
                        </div>

                        <div className="form-group">
                            <label>Google Review Count</label>
                            <input
                                type="number"
                                name="googleReviewCount"
                                value={formData.googleReviewCount}
                                onChange={handleChange}
                                min="0"
                                placeholder="100"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Status & Settings</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="available">Available</option>
                                <option value="unavailable">Unavailable</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="verified"
                                    checked={formData.verified}
                                    onChange={handleChange}
                                    disabled
                                />
                                Verified (Auto-approved for admin)
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="premium"
                                checked={formData.premium}
                                onChange={handleChange}
                            />
                            Premium Badge
                        </label>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="namazFacility"
                                    checked={formData.namazFacility}
                                    onChange={handleChange}
                                />
                                Namaz Facility Available
                            </label>
                            <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                Restaurant has prayer facility for customers
                            </small>
                        </div>

                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="womenFacility"
                                    checked={formData.womenFacility}
                                    onChange={handleChange}
                                />
                                Facility for Women
                            </label>
                            <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                Prayer area available for women
                            </small>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/restaurants')}>
                        Cancel
                    </button>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Adding...' : 'Add Restaurant'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddRestaurant;
